// Help-tekstit per osio. Pidetään erillään route:eista jotta tekstit
// ovat helposti haettavia ja päivitettävissä yhdessä paikassa.
//
// Kaikki tekstit ovat suomeksi (käyttöliittymän kieli).

import type { ReactNode } from "react";

export const HELP: Record<string, { title: string; body: ReactNode }> = {
  // ── /asetukset ───────────────────────────────────────────────────────
  profile: {
    title: "Profiili",
    body: (
      <>
        <p>
          Näet kratti-tilisi perustiedot. Sähköposti ja nimi tulevat
          rekisteröitymisestä, eikä niitä voi vaihtaa vaiheessa 3.
        </p>
        <p>
          <strong>Roolit</strong> kertovat, mitä oikeuksia käyttäjälläsi on:
        </p>
        <ul className="ml-4 list-disc space-y-1">
          <li>
            <code>photographer</code> — tavallinen käyttäjä, näkee omat
            assetit ja jaetut projektit
          </li>
          <li>
            <code>manager</code> — näkee kaikkien käyttäjien projektit,
            voi exportoida ja tehdä aktiivisesti hallintaa
          </li>
        </ul>
        <p>
          <strong>Age-julkinen avain</strong> on backupien kryptauksessa
          käytetty avain. Daemon luo sen ensimmäisessä käynnistyksessä ja
          lataa tämän palvelimelle. Avainta ei tarvitse muistaa — daemon
          käyttää sitä automaattisesti, mutta jos joudut palauttamaan
          backuppeja uudella koneella, tarvitset *yksityisen* avaimen
          tiedostosta <code className="mx-1">~/.config/sops/age/keys.txt</code>.
        </p>
      </>
    ),
  },
  projects: {
    title: "Projektit",
    body: (
      <>
        <p>
          Lista projekteista joihin sinulla on pääsy. Owner-rooli sallii
          arkistoinnin ja exportin; viewer/editor-roolit sallivat lukemisen
          ja kuratoinnin.
        </p>
        <p>Projektit voi luoda kahdella tavalla:</p>
        <ul className="ml-4 list-disc space-y-1">
          <li>
            Manuaalisesti <code>+ Uusi projekti</code> -painikkeella
            Projektit-näkymässä
          </li>
          <li>
            Automaattisesti — daemon havaitsee{" "}
            <code>.kratti-project.yaml</code>-sidecar-tiedoston jossakin
            source-rootin alikansiossa, tai{" "}
            <code>~/ProjectVault/</code>-konvention perusteella
          </li>
        </ul>
      </>
    ),
  },
  account: {
    title: "Tilin hallinta",
    body: (
      <>
        <p>
          Salasanan vaihto, sähköpostin päivitys ja logout tulevat vaiheessa
          3+. Vaiheen 2.5 versiossa profiili on read-only.
        </p>
        <p>
          Jos haluat muutoksia, ota yhteyttä manageriin tai tee se suoraan
          databasen kautta (tuki).
        </p>
      </>
    ),
  },

  // ── /levyt ───────────────────────────────────────────────────────────
  sourceRoots: {
    title: "Source-rootit",
    body: (
      <>
        <p>
          <strong>Source-rootit ovat backup-skooppi</strong> — daemon seuraa
          näitä polkuja ja sisällyttää niiden tiedostot Kopia-snapshot:eihin.
          Polun ulkopuolella olevat tiedostot eivät ole backupissa.
        </p>
        <p>
          Source-root EI ole sama asia kuin projekti. Voit lisätä source-rootin
          (esim. USB-levyn) ilman, että sen alta tulee yhtään projektia —
          assetit ovat silti backupissa "orphan"-tilassa, ja voit ryhmitellä
          ne projekteiksi myöhemmin.
        </p>
        <p>
          <strong>Whitelist (turvallisuus):</strong> daemon hyväksyy vain polut
          jotka alkavat <code className="mx-1">/Users/</code>,
          <code className="mx-1">/Volumes/</code>,
          <code className="mx-1">/home/</code> tai
          <code className="mx-1">/mnt/</code>. Yritys lisätä
          <code className="mx-1">/etc</code> tai
          <code className="mx-1">/System</code> ohitetaan automaattisesti
          (varoitusrivi lokiin).
        </p>
        <p className="text-xs text-slate-500">
          Muutos vaikuttaa daemoniin alle 60 s sisällä heartbeat-syklissä.
          Vanhat polut jotka poistetaan: assetit pysyvät databasessa, mutta
          niitä ei enää backupata uusiksi muutoksiksi.
        </p>
      </>
    ),
  },
  pacing: {
    title: "Pacing-mode",
    body: (
      <>
        <p>
          Säätelee miten paljon kuormaa daemon aiheuttaa Mac:lla
          thumbnail-generoinnin (ja mahdollisesti muiden taustatehtävien)
          aikana. Backup-snapshot:t Kopialla eivät säädellä tämän mukaan.
        </p>
        <ul className="ml-4 list-disc space-y-2">
          <li>
            <strong>Polite</strong> — 1 task kerrallaan, 200 ms väli,
            loadavg-pause 4.0:lla. <em>Default</em>. Käyttäjä ei huomaa
            daemonin kuormaa. Backfill-kausi pitkä mutta tasainen.
          </li>
          <li>
            <strong>Balanced</strong> — 2 rinnakkaista, 100 ms väli. Järkevä
            kun haluat näkyvää mutta hallittua etenemistä.
          </li>
          <li>
            <strong>Fast</strong> — 4 rinnakkaista, ei viivettä. Käytä kun
            haluat kuvat näkyviin pikaisesti, ja kone ei ole työkuormassa.
          </li>
        </ul>
        <p className="text-xs text-slate-500">
          Vaiheen 2.5 v1:ssä moodin vaihto vaatii daemon-restartin. Vaihe
          3:ssa tulee live-uudelleenkonfigurointi.
        </p>
      </>
    ),
  },
  conventions: {
    title: "Projekti-konventiot",
    body: (
      <>
        <p>
          Konventio on path-templaatti joka kertoo, miten daemon tunnistaa
          alikansiot projekteiksi automaattisesti.
        </p>
        <p>Esim.:</p>
        <ul className="ml-4 list-disc space-y-1">
          <li>
            <code>/Users/kimmos/Movies/&#123;project&#125;/</code> — kaikki
            suorat alikansiot <code>~/Movies/</code>:n alla ovat projekteja,
            nimi tulee kansion nimestä
          </li>
          <li>
            <code>/Users/kimmos/Pictures/&#123;date:YYYY-MM-DD&#125;/</code> —
            päiväys-konventio (esim. <code>2026-02-05/</code> on projekti
            "2026-02-05")
          </li>
        </ul>
        <p>
          <strong>Autocreate-checkbox:</strong> jos päällä, daemon kirjoittaa
          <code className="mx-1">.kratti-project.yaml</code>-tiedoston
          jokaiseen matchaavaan kansioon (idempotentti — ei ylikirjoita jo
          olemassa olevaa). Yamlin id-kenttä UUID generoidaan kerran.
        </p>
        <p className="text-xs text-slate-500">
          Konventiot ovat opt-in. Default = ei konventiota, projektit luodaan
          manuaalisesti tai sidecar:lla.
        </p>
      </>
    ),
  },
  disks: {
    title: "Levyt",
    body: (
      <>
        <p>Daemon raportoi koneen levyt 60 s välein:</p>
        <ul className="ml-4 list-disc space-y-1">
          <li>
            <strong>Sisäinen</strong> — Macin päälevy (esim. Macintosh HD)
          </li>
          <li>
            <strong>USB</strong> — ulkoiset USB- tai Thunderbolt-levyt joiden
            tunnistus tulee drivelist-kirjastosta
          </li>
        </ul>
        <p>
          Listalla näkyvät vain levyt joiden viimeisin raportointi on alle
          10 min vanha. Jos kytket USB-levyn irti, se näkyy listalla tämän
          10 min ajan ja siirtyy sitten "vanhentumis-tilaan" (tieto säilyy
          databasessa, mutta UI ei näytä sitä enää).
        </p>
        <p className="text-xs text-slate-500">
          Vaiheen 3 USB-import-flow (ADR-009) käyttää tätä listaa
          tunnistamaan kytkettyjä taustalevyjä automaattiseen ingestoon.
        </p>
      </>
    ),
  },
};
